import { createClient, type SupabaseClient } from '@supabase/supabase-js'
import { Browser } from '@capacitor/browser'
import { getAuthCallbackRedirectTo, isNativeAuthRedirect } from './authRedirect'
import { createSupabaseAuthStorage } from './supabaseStorage'
import { isNativeIOS } from '../mobile/capacitor'
import { AppleSignIn } from '../mobile/appleSignIn'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined

if (!supabaseUrl || !supabaseAnonKey) {
  // In development, log a helpful warning so developers know what to set
  // Avoid throwing to not break static builds where envs are injected at deploy time

  console.warn(
    '[Supabase] Missing VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY. ' +
      'Set them in your frontend .env (see docs/SUPABASE_SETUP.md)'
  )
}

const authStorage = createSupabaseAuthStorage()

export const supabase: SupabaseClient = createClient(supabaseUrl ?? '', supabaseAnonKey ?? '', {
  auth: {
    ...(authStorage ? { storage: authStorage } : {}),
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: !isNativeAuthRedirect(),
    flowType: 'pkce',
  },
})

/**
 * Sign in with Google OAuth
 * Redirects user to Google consent screen, then back to the app
 * Uses auth callback page to ensure session is fully loaded before dashboard redirect
 */
export const signInWithGoogle = async () => {
  const useNativeRedirect = isNativeAuthRedirect()
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      // Redirect to callback page first to ensure session is fully loaded
      redirectTo: getAuthCallbackRedirectTo(),
      skipBrowserRedirect: useNativeRedirect,
      // Optioneer sign-out does not (and should not) clear Google's browser session.
      // Always show Google's chooser so a returning user can switch accounts.
      queryParams: {
        prompt: 'select_account',
      },
    },
  })

  if (error) throw error

  if (useNativeRedirect) {
    if (!data.url) {
      throw new Error('Unable to start Google sign-in. Please try again.')
    }

    await Browser.open({
      url: data.url,
      presentationStyle: 'fullscreen',
    })
  }

  return data
}

/**
 * Sign in natively with Apple on iOS, then exchange Apple's ID token for a
 * Supabase session. Apple only returns the user's name on the first grant, so
 * preserve it immediately when it is available.
 */
export const signInWithApple = async () => {
  if (!isNativeIOS()) {
    throw new Error('Sign in with Apple is only available in the iOS app.')
  }

  const credential = await AppleSignIn.signIn()
  const { data, error } = await supabase.auth.signInWithIdToken({
    provider: 'apple',
    token: credential.identityToken,
    nonce: credential.nonce,
  })

  if (error) throw error

  const fullName = credential.fullName?.trim()
  if (fullName && data.user) {
    const [metadataResult, profileResult] = await Promise.allSettled([
      supabase.auth.updateUser({ data: { full_name: fullName } }),
      supabase.from('profiles').update({ full_name: fullName }).eq('id', data.user.id),
    ])

    if (
      (metadataResult.status === 'fulfilled' && metadataResult.value.error) ||
      (profileResult.status === 'fulfilled' && profileResult.value.error) ||
      metadataResult.status === 'rejected' ||
      profileResult.status === 'rejected'
    ) {
      console.warn('[Auth] Apple sign-in succeeded, but the first-login name could not be saved.')
    }
  }

  return data
}
